/**
 * Quick actions on the check-in overlay — "I'm OK, but…".
 *
 * The overlay asks one question and takes one tap. That is the whole point of
 * it: the person answering may be unwell, elderly, or holding the phone in one
 * hand. So these do not become a form. They are flags, tapped or not, and the
 * big circle stays the only thing that sends.
 *
 * ── Why flags and not scales ──────────────────────────────────────────────
 * The check-in sheet records PainLevel 0–10 and Mood/Energy/Appetite 1–5. A
 * chip cannot express a number, and inventing one — filing "in pain" as a
 * 5/10 nobody said — would put a fabricated reading into the same store the
 * personal baselines are computed from. The band would then be built partly
 * from numbers the recipient never gave.
 *
 * So: anything that is genuinely binary is sent as structured data
 * (DizzinessEpisode is a real 0/1 type), and everything else rides in the
 * note, which lands verbatim on the timeline where the care team reads it.
 * Nothing is guessed. The full sheet remains the place to record a level.
 *
 * ── What is deliberately absent ───────────────────────────────────────────
 * There is no "I fell". A fall is not a symptom to log and look at later, it
 * is a reason to contact someone, and offering it here — where the only
 * outcome is a row on a screen — would let a person on the floor tap a button
 * that summons nobody. That needs an escalation path before it needs a chip.
 */

export type QuickActionKey = 'dizzy' | 'pain' | 'tired' | 'low_mood';

export type QuickAction = {
  key: QuickActionKey;
  label: string;
  icon: [string, string];
  /** Plain-language phrase used in the note sent to the care team. */
  phrase: string;
};

export const QUICK_ACTIONS: QuickAction[] = [
  { key: 'dizzy', label: 'Dizzy', icon: ['fad', 'wave-pulse'], phrase: 'dizzy' },
  { key: 'pain', label: 'In pain', icon: ['fad', 'bolt'], phrase: 'in pain' },
  { key: 'tired', label: 'Very tired', icon: ['fad', 'moon'], phrase: 'very tired' },
  { key: 'low_mood', label: 'Low mood', icon: ['fad', 'face-smile'], phrase: 'in low mood' },
];

/** Structured types a one-tap flag can honestly fill in. */
const STRUCTURED: Partial<Record<QuickActionKey, { type: string; value: number }>> = {
  dizzy: { type: 'DizzinessEpisode', value: 1 },
};

export type SymptomPayload = {
  entries: Array<{ type: string; value: number }>;
  note: string;
};

/** Oxford-free list: "dizzy and in pain", "dizzy, in pain and very tired". */
function joinPhrases(phrases: string[]): string {
  if (phrases.length <= 1) return phrases[0] || '';
  return `${phrases.slice(0, -1).join(', ')} and ${phrases[phrases.length - 1]}`;
}

/**
 * The manual-entry body for a set of selected flags, or null when none are.
 *
 * Null rather than an empty payload: a plain "I'm OK" must not post a second
 * request that says nothing, and manual-entry rejects an empty entries array
 * anyway.
 */
export function buildSymptomPayload(selected: QuickActionKey[]): SymptomPayload | null {
  const keys = QUICK_ACTIONS.map((a) => a.key).filter((k) => selected.includes(k));
  if (!keys.length) return null;

  const entries = keys
    .map((k) => STRUCTURED[k])
    .filter(Boolean) as Array<{ type: string; value: number }>;

  const phrases = keys.map((k) => QUICK_ACTIONS.find((a) => a.key === k)!.phrase);
  // Past tense and attributed, so the timeline never reads as an observation
  // somebody else made about her.
  const note = `Checked in and reported feeling ${joinPhrases(phrases)}.`;

  return { entries, note };
}
